import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { SubmitButton } from "components/common/buttons/SubmitButton";
import { fetchLanguages } from "slices/languageSlice";
import { languageSelector } from "slices/languageSlice";
import { setError } from "slices/dashboardSlice";
import { AsyncPaginate } from 'react-select-async-paginate';
import { selectCustomStyles } from 'components/common/Select';
import FullPageLoader from "components/common/FullPageLoader";
import ListTable from "components/interpreterAccess/dashboard/ListTable";
import { InterpreterDashboardSelector } from "slices/interpreter/dashboardSlice";
import { fetchInterpreterDashboardAnalytics } from "slices/interpreter/dashboardSlice";
import { fetchInterpreterDashboardAppointments, downloadDashboardReport } from "slices/interpreter/dashboardSlice";
import { interpreterLegends, LegendButton } from "components/common/TableColor";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import DateRangeFilter from "components/common/DateRangeFilter";
import { fetchSchools } from "slices/schoolSlice";
import { schoolSelector } from "slices/schoolSlice";


function Dashboard() {
  const dispatch = useDispatch()
  const { interpreterDashboard: dashboard, last_page: totalPages, loader } = useSelector(InterpreterDashboardSelector);
  const lunchRecords = dashboard?.lunchRecords ?? [];
  const { language: languages } = useSelector(languageSelector);
  const { school: schools } = useSelector(schoolSelector);
  const [downloading, setDownloading] = useState(false);
  const [showLunch, setShowLunch] = useState(true);
  const [activeLegends, setActiveLegends] = useState([0]);
  const [statusValues, setStatusValues] = useState([]);
  const [languageState, setLanguageState] = useState([]);
  const navigate = useNavigate();
  const tableRef = useRef(null);

  const today = moment().format('YYYY-MM-DD');

  const defaultValues = {
    start_date: new Date(),
    end_date: new Date(),
    appointment_id: "",
    school_id: null,
    type: null,
    language_ids: [],
  };

  const { clearErrors, handleSubmit, watch, formState: { errors }, control, setValue, reset, getValues } = useForm({
    defaultValues,
  });
  const baseParams = { pageLength: 10, sortBy: "date", sortDirection: "desc", type: [0, 1, 4] };
  const todayParams = { ...baseParams, start_date: today, end_date: today };
  const [filters, setFilters] = useState(todayParams);

  useEffect(() => {
    dispatch(fetchInterpreterDashboardAnalytics());
    dispatch(fetchInterpreterDashboardAppointments(1, todayParams));
  }, [dispatch]);

  // #region Language dropdown
  const filteredLanguagesLoadOptions = async (inputValue, loadedOptions, { page }) => {
    const result = await dispatch(fetchLanguages(page, { pageLength: 20, sortBy: "id", sortDirection: "asc", search: inputValue }, true, true));
    return {
      options: result.data.map((item) => ({
        value: item.id,
        label: item.name,
      })),
      hasMore: page !== result?.last_page,
      additional: { page: page + 1 },
    };
  };
  //#endregion

  // #region School dropdown
  const filteredSchools = React.useMemo(() =>
    schools?.map(s => ({ value: s?.id, label: s?.name })), [schools]);

  const filteredSchoolLoadOptions = async (inputValue, loadedOptions, { page }) => {
    const result = await dispatch(fetchSchools(page, { pageLength: 20, sortBy: "id", sortDirection: "asc", search: inputValue }, true, true));
    return {
      options: result.data.map((item) => ({ value: item.id, label: item.name })),
      hasMore: page !== result?.last_page,
      additional: { page: page + 1 },
    };
  };
  //#endregion

  // #region Legends
  const filteredStatuses = React.useMemo(() => {
    const temp_array = [...interpreterLegends];
    temp_array.unshift({ value: "all", name: "All", id: 0, color: "#3176A0" });
    return temp_array;
  }, [interpreterLegends]);

  // Centralised param builder — always produces YYYY-MM-DD dates
  const buildParams = (formValues, statuses) => ({
    ...baseParams,
    start_date: formValues.start_date ? moment(formValues.start_date).format('YYYY-MM-DD') : undefined,
    end_date: formValues.end_date ? moment(formValues.end_date).format('YYYY-MM-DD') : undefined,
    appointment_id: formValues.appointment_id || undefined,
    school_id: formValues.school_id || undefined,
    // If a specific type is selected use it; otherwise default to [0,1,4]
    type: (formValues.type !== null && formValues.type !== undefined) ? [formValues.type] : [0, 1, 4],
    language_ids: formValues.language_ids?.length ? formValues.language_ids : undefined,
    status: statuses?.length ? statuses : undefined,
  });

  const handleActiveLegend = (id, value) => {
    const isAll = value === "all";
    const isAlreadyActive = activeLegends.includes(id);
    if (isAll || isAlreadyActive) {
      // Resetting to All — show lunch too
      setActiveLegends([0]);
      setStatusValues([]);
      setShowLunch(true);
      const params = buildParams(getValues(), []);
      setFilters(params);
      dispatch(fetchInterpreterDashboardAppointments(1, params));
    } else if (value === 'day_off') {
      // Day Off: filter by type=4, not by status
      setActiveLegends([id]);
      setStatusValues([]);
      setShowLunch(false);
      const params = { ...buildParams(getValues(), []), type: [4] };
      setFilters(params);
      dispatch(fetchInterpreterDashboardAppointments(1, params));
    } else {
      // Specific status filter — hide lunch
      setActiveLegends([id]);
      setStatusValues([value]);
      setShowLunch(false);
      const params = buildParams(getValues(), [value]);
      setFilters(params);
      dispatch(fetchInterpreterDashboardAppointments(1, params));
    }
  };
  //#endregion

  const onSubmit = (values) => {
    dispatch(setError({}));
    const params = buildParams(values, statusValues);
    setFilters(params);
    dispatch(fetchInterpreterDashboardAppointments(1, params));
    clearErrors();
  };

  const handleResetFields = () => {
    reset(defaultValues);
    setLanguageState([]);
    setActiveLegends([0]);
    setStatusValues([]);
    setFilters(todayParams);
    dispatch(fetchInterpreterDashboardAppointments(1, todayParams));
    clearErrors();
  };

  const downloadBlobFile = (blob, filename = "appointments.xlsx") => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadReport = () => {
    setDownloading(true);
    const currentFilters = { ...getValues(), status: statusValues.length ? statusValues : undefined };
    dispatch(downloadDashboardReport(currentFilters, (success, res) => {
      setDownloading(false);
      if (!success) {
        Swal.fire({ title: "Error!", text: (res && res.message) || "Failed to download report", icon: "error", confirmButtonColor: "#d33" });
        return;
      }
      try {
        if (res instanceof Blob) {
          downloadBlobFile(res);
        } else if (res && res.blob) {
          downloadBlobFile(res.blob, res.filename || "dashboard_report.xlsx");
        } else if (typeof res === "string" && res.startsWith("data:")) {
          const arr = res.split(",");
          const mimeMatch = arr[0].match(/:(.*?);/);
          const mime = mimeMatch ? mimeMatch[1] : "application/octet-stream";
          const bstr = atob(arr[1]);
          let n = bstr.length;
          const u8 = new Uint8Array(n);
          while (n--) u8[n] = bstr.charCodeAt(n);
          downloadBlobFile(new Blob([u8], { type: mime }));
        } else if (typeof res === "string") {
          const a = document.createElement("a");
          a.href = res;
          a.target = "_blank";
          a.download = "";
          document.body.appendChild(a);
          a.click();
          a.remove();
        }
        Swal.fire({ title: "Success!", text: "Report downloaded successfully.", icon: "success", confirmButtonColor: "#3085d6" });
      } catch (err) {
        console.error("Download error:", err);
        Swal.fire({ title: "Error!", text: "Could not process downloaded file", icon: "error", confirmButtonColor: "#d33" });
      }
    }));
  };

  const applyAnalyticFilter = (type, startDate, endDate) => {
    if (type !== 0 && type !== 1) return; // only handle appt types in this table
    const start = moment(startDate).format('YYYY-MM-DD');
    const end = moment(endDate).format('YYYY-MM-DD');
    setValue('start_date', new Date(startDate));
    setValue('end_date', new Date(endDate));
    setActiveLegends([0]);
    setStatusValues([]);
    const params = {
      ...baseParams,
      start_date: start,
      end_date: end,
      type: [type],
    };
    setFilters(params);
    dispatch(fetchInterpreterDashboardAppointments(1, params));
    tableRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const viewAppointments = (type) => {
    if (type === 0 || type === 1) {
      applyAnalyticFilter(type, new Date(), new Date());
    } else if (type === 2) {
      navigate("/interpreter/billingual-requests", { state: { filterByToday: true } });
    } else if (type === 3) {
      navigate("/interpreter/translations", { state: { filterByToday: true } });
    }
  };

  const nextFourteenDaysAppointments = (type) => {
    if (type === 0 || type === 1) {
      applyAnalyticFilter(type, moment().add(1, 'day').toDate(), moment().add(14, 'days').toDate());
    } else if (type === 2) {
      navigate("/interpreter/billingual-requests", { state: { filterByFourteen: true } });
    } else if (type === 3) {
      navigate("/interpreter/translations", { state: { filterByFourteen: true } });
    }
  };

  return (
    <>
      <div className="content">
        <div className="row_main_head">
          <h2 className="main_heading">Dashboard</h2>
        </div>

        <div className="row_dash_1">
          <div className="card_table">
            <div className="interpreter p-4">
              <h2>Today</h2>
            </div>
            <div className="Table_data_show pt-0">
              <table>
                <thead>
                  <tr>
                    <th className="col-6">Today</th>
                    <th className="col-3 text-center">Registered</th>
                    <th className="col-3 text-center">To Cover</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard?.analytics?.todays_data?.map((service, index) => (
                    <tr className="cursorPointer" key={index} onClick={() => viewAppointments(service?.value)}>
                      <td>{service?.type}</td>
                      <td>{service?.assigned}</td>
                      <td>{service?.pending}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card_table">
            <div className="interpreter p-4">
              <h2>Next 14 Days</h2>
            </div>
            <div className="Table_data_show pt-0">
              <table>
                <thead>
                  <tr>
                    <th className="col-6">Today</th>
                    <th className="col-3 text-center">Registered</th>
                    <th className="col-3 text-center">To Cover</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard?.analytics?.fourteen_days?.map((service, index) => (
                    <tr className="cursorPointer" key={index} onClick={() => nextFourteenDaysAppointments(service?.value)}>
                      <td>{service?.type}</td>
                      <td>{service?.assigned}</td>
                      <td>{service?.pending}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="card_table">
          <div className="button-sm forLegends p-3 gap-1">
            <LegendButton activeIds={activeLegends} options={filteredStatuses} handleClick={handleActiveLegend} />
            <div className={`border_btn ${showLunch ? 'active_border' : ''}`}>
              <button
                type="button"
                className="button-bu"
                style={{ backgroundColor: '#2e7d32' }}
                onClick={() => {
                  const next = !showLunch;
                  setShowLunch(next);
                  if (next) {
                    // deselect all status legends when Lunch is turned on
                    setActiveLegends([0]);
                    setStatusValues([]);
                  }
                }}
              >
                <p>Lunch</p>
              </button>
            </div>
          </div>
          <form className="search_form_ad" onSubmit={handleSubmit(onSubmit)}>
            <div className="row-primary dahboard_filter appointments">
              <DateRangeFilter control={control} errors={errors} />

              <div className="row-primary">
                <h2>Appointment ID</h2>
                <Controller
                  name="appointment_id"
                  control={control}
                  render={({ field }) => (
                    <div className="input-basis basic_bases">
                      <input {...field} type="text" className="input-in type_inp" placeholder="Enter ID" />
                    </div>
                  )}
                />
              </div>

              <div className="row-primary">
                <h2>School</h2>
                <Controller
                  name="school_id"
                  control={control}
                  render={({ field }) => (
                    <div style={{ width: "100%" }}>
                      <AsyncPaginate
                        menuPlacement="auto"
                        styles={{ ...selectCustomStyles }}
                        value={field.value ? filteredSchools?.find((opt) => field.value == opt.value) : ""}
                        loadOptions={filteredSchoolLoadOptions}
                        isClearable
                        escapeClearsValue
                        onChange={(obj) => field.onChange(obj?.value ?? null)}
                        additional={{ page: 1 }}
                      />
                    </div>
                  )}
                />
              </div>

              <div className="row-primary">
                <h2>Type</h2>
                <Controller
                  name="type"
                  control={control}
                  render={({ field }) => (
                    <div style={{ width: "100%" }}>
                      <AsyncPaginate
                        styles={selectCustomStyles}
                        options={[
                          { value: 0, label: "Interpreting Request" },
                          { value: 1, label: "After Hours Request" },
                        ]}
                        isClearable
                        placeholder="Select Type"
                        onChange={(option) => field.onChange(option !== null ? option.value : null)}
                        value={
                          field.value !== null && field.value !== undefined
                            ? [
                                { value: 0, label: "Interpreting Request" },
                                { value: 1, label: "After Hours Request" },
                              ].find((opt) => opt.value === field.value)
                            : null
                        }
                      />
                    </div>
                  )}
                />
              </div>

              <div className="row-primary">
                <h2>Languages</h2>
                <Controller
                  name="language_ids"
                  control={control}
                  render={({ field }) => (
                    <div style={{ width: "100%" }} className="multiselect">
                      <AsyncPaginate
                        menuPlacement="auto"
                        isMulti
                        styles={{ ...selectCustomStyles }}
                        value={languageState}
                        loadOptions={filteredLanguagesLoadOptions}
                        isClearable
                        escapeClearsValue
                        onChange={(selected) => {
                          setLanguageState(selected);
                          field.onChange((selected || []).map((opt) => opt.value));
                        }}
                        additional={{ page: 1 }}
                      />
                    </div>
                  )}
                />
              </div>
            </div>

            <div className="d-flex align-items-center justify-content-between btns_area">
              <div className="row-primary row_10">
                <SubmitButton handleSubmit={handleSubmit(onSubmit)} buttonType="button" btnText="Search" className="green" />
                <SubmitButton buttonType="button" className="lightBlue" btnText="Reset" handleSubmit={handleResetFields} />
              </div>
              <button
                type="button"
                style={{ height: "fit-content", marginLeft: "10px" }}
                className="main_button"
                onClick={handleDownloadReport}
                disabled={downloading}
              >
                {downloading ? "Exporting..." : "Export to Excel"}
              </button>
            </div>

            <div ref={tableRef}>
              <ListTable data={dashboard?.appointments} lunchRecords={lunchRecords} showLunch={showLunch} filtration={filters} />
            </div>
          </form>
        </div>

      </div>

      {loader && <FullPageLoader />}
    </>
  );
}

export default Dashboard;
